import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BackToView } from "../../common/http";
import { AppRoutes } from "../../common/routesUtils";
import ErrorMessage, { ReloadAction } from "../../components/common/ErrorMessage";
import { PublicView } from "../../components/layout/PublicView";
import { usePageConfiguration } from "../../context/PageConfigrations/PageConfigurationProvider";
import { useAuthentication } from "../../hooks/useAuthentication";

interface Props {
	httpStatusCode?: number;
	backToSection?: BackToView;
}

/**
 * ErrorView renders a view that contains:
 * - a meaningful error title and message for the given httpStatusCode;
 * - a generic error title and message if httpStatusCode is undefined;
 * - an optional button that redirects users back to specific app sections.
 *
 * @param httpStatusCode the optional HTTP status code that is used to render appropriate
 *                       error title and message; if undefined, a generic error message will
 *                       be presented to the end user
 * @param backToSection  if set, the component will render a "back-to-app section" that will
 *                       redirect users to specific sections "/"; this value defaults to undefined
 * @returns              the error view
 */
const ErrorView: FC<Props> = ({ httpStatusCode, backToSection }) => {
	const { t } = useTranslation();
	const { signOut } = useAuthentication();
	const navigateTo = useNavigate();
	const { clearAllPageConfigurations } = usePageConfiguration();

	const backToHome = {
		title: t("back to/home"),
		onClick: () => {
			navigateTo(AppRoutes.ResultsOverview);
		},
	};

	const backToSignIn = {
		title: t("back to/sign in"),
		onClick: () => {
			clearAllPageConfigurations();
			signOut();
			navigateTo(AppRoutes.SignIn);
		},
	};

	// Set the main accept action.
	let actions: Array<ReloadAction> = [];
	switch (backToSection) {
		case BackToView.Home:
			actions.push(backToHome);
			break;
		case BackToView.HomeOrSignIn:
			actions.push(backToHome, backToSignIn);
			break;
		case BackToView.SignIn:
			actions.push(backToSignIn);
			break;
		default:
			break;
	}

	return (
		<PublicView height={"100vh"}>
			<ErrorMessage httpStatusCode={httpStatusCode} actions={actions} />
		</PublicView>
	);
};

export default ErrorView;
